import React from "react";
import { motion } from "framer-motion";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  MessageSquare, Star, Bot, Calendar, ShoppingBag, CreditCard,
  UserCheck, Users, PhoneMissed, BookOpen, ArrowUpRight
} from "lucide-react";

const CRAFTSMAN_IMG = "/images/craftsman-comparison.png";
const PAUL_HUGHES_IMG = "/images/paul-hughes-services.png";
const CTA_IMAGE = "/images/cta-image.png";
const CTA_URL = "https://diyb.ca/contact-diyb/";

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const services = [
  {
    icon: MessageSquare,
    title: "Mobile Outreach",
    subtitle: "SMS Marketing & Mobile Campaigns",
    description: "Your customers keep their phone within reach all day. Coupons, reminders, and review requests land as a text they actually open, instead of an email they ignore.",
    highlight: "Land in their hand, not their spam folder",
    link: "https://diyb.ca/sms-marketing-mobile-campaigns",
  },
  {
    icon: Star,
    title: "Trust Building",
    subtitle: "Smart Review Management",
    description: "A five-star rating is currency, and silence costs you calls. Smart Review Management turns your happy customers into public proof.",
    highlight: "Turn silence into 5-star proof",
    link: "https://diyb.ca/smart-review-management",
  },
  {
    icon: Bot,
    title: "Always-On Support",
    subtitle: "AI-Powered Customer Service",
    description: "Customers ask questions at night, on weekends, standing in your parking lot. AI-powered answers reply 24/7, book appointments, and capture the lead while you sleep.",
    highlight: "Your 24/7 team member",
    link: "https://diyb.ca/ai-powered-customer-service",
  },
  {
    icon: Calendar,
    title: "Service Excellence",
    subtitle: "Appointment Booking Systems",
    description: "No-shows steal time and confidence. Let customers book anytime with helpful SMS reminders, so there is no phone tag and no double-bookings.",
    highlight: "Reclaim stolen hours",
    link: "https://diyb.ca/appointment-booking-systems",
  },
  {
    icon: ShoppingBag,
    title: "Online Sales",
    subtitle: "Simple Online Stores",
    description: "This is not a giant store, just a clean way to collect orders and payments online without the complexity of a big e-commerce build.",
    highlight: "Sell simply, sell cleanly",
    link: "https://diyb.ca/simple-online-stores",
  },
  {
    icon: CreditCard,
    title: "Get Paid",
    subtitle: "Mobile Payment Solutions",
    description: "Take payments and send professional invoices right from your phone. Money moves the moment the job is done, wherever you are.",
    highlight: "Get paid on the spot",
    link: "https://diyb.ca/mobile-payment-solutions",
  },
  {
    icon: UserCheck,
    title: "Lead Growth",
    subtitle: "Lead Generation & Win-Back Systems",
    description: "Most businesses forget past customers, not on purpose, the week just fills up. These systems follow up automatically, attract new leads, and bring old ones back.",
    highlight: "Bring past customers back",
    link: "https://diyb.ca/lead-generation-win-back-systems",
  },
  {
    icon: Users,
    title: "Customer Management",
    subtitle: "Customer Management Tools (CRM)",
    description: "Every customer, every conversation, every follow-up in one place. Track your pipeline so nobody slips through the cracks and no opportunity goes cold.",
    highlight: "Never miss a follow-up",
    link: "https://diyb.ca/customer-management-crm",
  },
  {
    icon: PhoneMissed,
    title: "Lead Recovery",
    subtitle: "Missed Call Recovery",
    description: "A missed call costs money and momentum, and the lead goes cold fast. Missed Call Recovery fires back a text in seconds and rescues the conversation.",
    highlight: "Rescue leads before they go cold",
    link: "https://diyb.ca/missed-call-recovery",
  },
  {
    icon: BookOpen,
    title: "Self-Service Support",
    subtitle: "Knowledge Base Systems",
    description: "Give customers a place to find answers instantly, 24/7. A good knowledge base cuts repeat questions and frees your team for the work that matters.",
    highlight: "Answers on demand, day or night",
    link: "https://diyb.ca/knowledge-base-systems",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-36 bg-[#020617]"
    >
      <div className="blueprint-grid absolute inset-0 opacity-10" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.p
          {...fadeInUp}
          transition={{ duration: 0.6 }}
          className="font-body text-fluid-caption tracking-[0.3em] uppercase text-gold mb-4"
        >
          What I Build
        </motion.p>

        <motion.h2
          {...fadeInUp}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-heading font-bold text-fluid-h2 uppercase tracking-tight text-slate-50 mb-4"
        >
          Smart Digital <span className="text-gold-gradient">Doorways</span>
        </motion.h2>

        <motion.p
          {...fadeInUp}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="font-body text-fluid-body text-slate-400 max-w-2xl mb-4"
        >
          A Smart Digital Doorway is not a brochure site. It is a working system
          that supports your business. It answers customers, captures leads,
          books appointments, follows up, and builds trust.
        </motion.p>

        <Separator className="bg-gold/20 mb-12 max-w-xs" />

        {/* Craftsman Image */}
        <motion.div
          className="mb-16 relative rounded-sm overflow-hidden border border-slate-700/50 shadow-2xl shadow-black/40 max-w-4xl mx-auto"
          {...fadeInUp}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <img
            src={CRAFTSMAN_IMG}
            alt="Custom-built for your business by a Master Craftsman - Paul Hughes, Doorways Into Your Business, serving Kingsville and the Windsor-Essex Region"
            data-testid="services-craftsman-image"
            className="w-full h-auto"
            loading="lazy"
          />
          <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-gold/40" />
          <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-gold/40" />
        </motion.div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, i) => {
            const Icon = service.icon;
            const isLarge = i === 0 || i === 5;

            return (
              <React.Fragment key={service.title}>
                <motion.div
                  data-testid={`service-card-${i}`}
                  className={`card-glow group relative bg-[#0f172a] border border-slate-800 rounded-sm p-7 hover:border-gold/30 transition-all duration-500 ${
                    isLarge ? "lg:col-span-2 lg:row-span-1" : ""
                  }`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                >
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-sm bg-gold/10 border border-gold/20 flex items-center justify-center mb-5 group-hover:bg-gold/20 transition-colors duration-300">
                    <Icon size={22} className="text-gold" />
                  </div>

                  {/* Title */}
                  <h3 className="font-heading font-bold text-fluid-h3 text-slate-50 mb-1 uppercase tracking-wide">
                    {service.title}
                  </h3>
                  <p className="font-accent italic text-gold/80 text-fluid-small mb-4">
                    {service.subtitle}
                  </p>

                  {/* Description */}
                  <p className="font-body text-slate-400 text-fluid-body mb-4">
                    {service.description}
                  </p>

                  {/* Highlight Tag */}
                  <div className="inline-block px-3 py-1 bg-gold/10 border border-gold/20 rounded-sm mb-5">
                    <span className="font-body text-fluid-caption tracking-wider uppercase text-gold">
                      {service.highlight}
                    </span>
                  </div>

                  {/* Learn More Link */}
                  <div className="relative z-10">
                    <a
                      href={service.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`service-link-${i}`}
                      className="relative z-10 inline-flex items-center gap-1.5 font-heading font-bold text-sm tracking-wider uppercase text-gold/70 hover:text-gold transition-colors duration-300 border-b border-gold/20 hover:border-gold/50 pb-0.5"
                    >
                      Learn More
                      <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </a>
                  </div>
                </motion.div>

                {/* Paul Hughes Image Card - inserted after Mobile Payment Solutions (index 5) */}
                {i === 5 && (
                  <motion.div
                    data-testid="paul-hughes-image-card"
                    className="card-glow group relative bg-[#0f172a] border border-slate-800 rounded-sm overflow-hidden hover:border-gold/30 transition-all duration-500 flex items-end"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: 0.35 }}
                  >
                    {/* Dark background layer */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0a192f] via-[#0f172a] to-[#1e293b]" />
                    <img
                      src={PAUL_HUGHES_IMG}
                      alt="Paul Hughes - Master Business Craftsman"
                      className="relative w-full h-auto object-contain"
                      loading="lazy"
                    />
                    {/* Subtle gradient overlay on edges */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/40 via-transparent to-[#0f172a]/20 pointer-events-none" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a]/30 via-transparent to-[#0f172a]/30 pointer-events-none" />
                  </motion.div>
                )}

                {/* CTA Image Card - inserted after Knowledge Base Systems (index 9) - spans 2 columns */}
                {i === 9 && (
                  <motion.a
                    href={CTA_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="cta-image-card"
                    className="card-glow group relative bg-[#0f172a] border border-slate-800 rounded-sm overflow-hidden hover:border-gold/30 transition-all duration-500 lg:col-span-2 flex items-center justify-center cursor-pointer"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                  >
                    {/* Light background to match image */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#f5f5f0] via-[#fafaf5] to-[#f0f0e8]" />
                    <img
                      src={CTA_IMAGE}
                      alt="Stop leaving money on the table - Get your free digital presence audit"
                      className="relative w-full h-auto object-contain"
                      loading="lazy"
                    />
                    {/* Subtle border glow on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-gold/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  </motion.a>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          className="mt-16 text-center"
          {...fadeInUp}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <a href={CTA_URL} target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="services-cta-button"
              className="bg-gold hover:bg-gold-300 text-navy-500 font-heading font-bold tracking-wider uppercase text-base px-8 py-5 rounded-sm transition-all duration-300 hover:shadow-[0_0_25px_-5px_rgba(212,175,55,0.5)]"
            >
              Get a Smart Digital Doorway Installed
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
